import React, { type FC } from 'react'
import { capitalize, isEmpty, isNil } from 'lodash'
import { Badge, Icon, Tooltip } from '@awell-health/design-system'
import { t } from 'i18next'

import { Activity, ActivityObjectType, ActivityStatus, nonUserActivity, type ActivityEventType } from '../../../types'
import { formatDate } from '@awell/ui-kit/utils'
import { ActivityResolution } from '../../../../../generated/types-orchestration'

interface Props {
  events: Array<ActivityEventType>
  stakeholder: string
  isAgentActivity: boolean
  isAgentActivityInitiator: boolean
  handleFilterReferenceId: () => void
  activity: Activity
}
const getBadgeVariant = (activity: Activity): string => {
  const { status, object, resolution } = activity
  switch (status) {
    case ActivityStatus.Active:
      return object.type === ActivityObjectType.PluginAction ||
        object.type === ActivityObjectType.ApiCall ||
        object.type === ActivityObjectType.EmrReport
        ? 'primary'
        : 'error'
    case ActivityStatus.Canceled:
    case ActivityStatus.Failed:
      return 'default'
    case ActivityStatus.Done:
      return resolution === ActivityResolution.Failure ? 'error' : 'success'
    case ActivityStatus.Expired:
      return 'error'
    default:
      return 'primary'
  }
}

const createStatus = (activity: Activity): string => {
  switch (activity.status) {
    case ActivityStatus.Active: {
      const isM2M = [
        ActivityObjectType.EmrReport,
        ActivityObjectType.PluginAction,
        ActivityObjectType.ApiCall
      ].includes(activity.object.type)
      if (isM2M) {
        return t('activityfeed:in_progress').toLocaleUpperCase()
      }
      return t('activityfeed:to_do').toLocaleUpperCase()
    }
    case ActivityStatus.Done:
      return activity.resolution === ActivityResolution.Failure
        ? t('activityfeed:failed').toLocaleUpperCase()
        : t('activityfeed:done').toLocaleUpperCase()
    case ActivityStatus.Canceled:
      return t('activityfeed:canceled').toLocaleUpperCase()
    case ActivityStatus.Failed:
      return t('activityfeed:failed').toLocaleUpperCase()
    case ActivityStatus.Expired:
      return t('activityfeed:expired').toLocaleUpperCase()
    default:
      return ''
  }
}

export const ActivityFooter: FC<Props> = props => {
  const { events, stakeholder, isAgentActivityInitiator, handleFilterReferenceId, isAgentActivity, activity } = props

  const activityStatus = createStatus(activity)
  const isUserActivity = !nonUserActivity.includes(activity.object.type)

  const completedBy = events.find(
    subActivity => !['Shelly', 'ava', 'Unknown', 'Anonymous'].includes(subActivity.content.avatar)
  )

  const stakeholdeName = isNil(completedBy) ? stakeholder : completedBy.content.avatar

  const timestamp = formatDate({
    isoDateStr: activity.date,
    dateFormat: 'dd MMM yyyy, h:mm:ss a' // 10 Jan 2025, 5:53:00 PM
  })

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        {!isAgentActivityInitiator && (
          <>
            {!isEmpty(activityStatus) && (
              <div>
                <Badge variant={getBadgeVariant(activity)} size="md">
                  {capitalize(activityStatus.toLowerCase())}
                </Badge>
              </div>
            )}
            {!isNil(stakeholdeName) && isUserActivity && (
              <div className="text-slate-600 flex gap-1 items-center text-sm">
                <Icon icon="RiUserFill" size={16} className="fill-slate-600" />
                {stakeholdeName}
              </div>
            )}
            {isAgentActivity && (
              <Tooltip datatip="Show all Agent activities">
                <div
                  className="text-slate-500 text-sm font-normal underline flex gap-1 items-center cursor-pointer"
                  onClick={() => handleFilterReferenceId()}
                >
                  <Icon icon="RiSparklingFill" size={20} className="fill-yellow-400" />
                  Initiated by Agent
                </div>
              </Tooltip>
            )}
          </>
        )}

        {isAgentActivityInitiator && (
          <div className="text-blue-700 text-sm font-medium cursor-pointer" onClick={() => handleFilterReferenceId()}>
            Show all Agent activities
          </div>
        )}
      </div>
      <div className="flex gap-1 justify-end items-center flex-shrink-0">
        <Icon icon="RiTimeFill" size={16} className="fill-slate-600" />
        <div className="text-neutral-500 text-sm">{timestamp}</div>
      </div>
    </div>
  )
}

ActivityFooter.displayName = 'ActivityFooter'
